import { useState } from "react";
import { z } from "zod";
import { appeler, ErreurApi, poserJeton } from "../api/client";
import { AvisSchema, useApi, type Avis } from "../api/hooks";
import { ListeAvis } from "../composants/Avis";
import { Tableau } from "../composants/Tableau";

const OffreSchema = z.object({
  id: z.string(),
  intitule: z.string(),
  description: z.string(),
  profil: z.string().nullable(),
  dateLimite: z.string().nullable(),
  publieeLe: z.string(),
});

const SessionCandidatSchema = z.object({
  accessToken: z.string(),
  nom: z.string(),
  role: z.literal("candidat"),
  expire: z.string(),
});

const CandidatureSchema = z.array(
  z.object({
    id: z.string(),
    offreId: z.string(),
    statut: z.string(),
    anneesExperienceDeclarees: z.number(),
    passageEnCabinet: z.boolean(),
    motivation: z.string().nullable(),
    envoyeeLe: z.string().nullable(),
    pieces: z.array(
      z.object({
        id: z.string(),
        type: z.string(),
        nomFichier: z.string(),
        taille: z.number(),
        periodeCouverte: z.string().nullable(),
      }),
    ),
    references: z.array(
      z.object({
        id: z.string(),
        entreprise: z.string(),
        poste: z.string(),
        nomContact: z.string(),
        telephone: z.string().nullable(),
        email: z.string().nullable(),
        estJoignable: z.boolean(),
      }),
    ),
  }),
);

/**
 * §5.N — Espace recrutement public.
 *
 * ⚠ C'est le SEUL module ouvert à Internet. Le message d'erreur de connexion
 * ne dit jamais si c'est l'adresse ou le mot de passe qui est faux : le front
 * ne l'enrichit pas.
 */
export function Recrutement() {
  const [connecte, setConnecte] = useState(false);
  const offres = useApi(["offres"], "/recrutement/offres", z.array(OffreSchema));

  return (
    <main className="recrutement">
      <header className="marque">
        <h1>La Maison de la Conformité</h1>
        <p className="marque__sous-titre">
          Agence de Notation Administrative — Comptable · Fiscal · Social
        </p>
      </header>

      <section>
        <h2>Offres ouvertes</h2>
        <Tableau
          colonnes={[
            { cle: "i", titre: "Poste", rendu: (o) => o.intitule },
            { cle: "d", titre: "Description", rendu: (o) => o.description },
            { cle: "p", titre: "Profil", rendu: (o) => o.profil ?? "—" },
            { cle: "l", titre: "Date limite", rendu: (o) => o.dateLimite ?? "—" },
          ]}
          lignes={offres.data ?? []}
          cle={(o) => o.id}
          vide="Aucune offre ouverte pour le moment."
        />
      </section>

      <section>
        <h2>Ce que votre dossier doit contenir</h2>
        <p className="resume">
          Un dossier incomplet ne peut pas être envoyé. Ces pièces sont vérifiées
          au moment de l'envoi.
        </p>
        <ul className="exigences">
          <li>Un curriculum vitae</li>
          <li>Une lettre de motivation</li>
          <li>
            Des <strong>bulletins de salaire justifiant cinq années d'ancienneté
            continue</strong> — une interruption de plus de deux mois rompt la
            continuité
          </li>
          <li>
            Au moins <strong>deux références professionnelles joignables</strong> :
            entreprise, poste, nom du contact, et un téléphone <em>ou</em> une
            adresse électronique. Une référence qu'on ne peut pas appeler ne vaut
            rien.
          </li>
          <li>Cinq années d'expérience déclarées au minimum</li>
        </ul>
        <p className="note">
          Un passage en cabinet d'expertise comptable est valorisé.
        </p>
      </section>

      {connecte ? <MonDossier /> : <Connexion onConnecte={() => setConnecte(true)} />}
    </main>
  );
}

function Connexion({ onConnecte }: { onConnecte: () => void }) {
  const [inscription, setInscription] = useState(false);
  const [email, setEmail] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [nom, setNom] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  async function soumettre(e: React.FormEvent) {
    e.preventDefault();
    setErreur(null);
    setMessage(null);

    try {
      if (inscription) {
        const r = await appeler(
          "/recrutement/inscription",
          z.object({ message: z.string() }),
          { method: "POST", body: JSON.stringify({ email, motDePasse, nom, prenoms: null, telephone: null }) },
        );
        setMessage(r.message);
        setInscription(false);
        return;
      }

      const s = await appeler("/recrutement/connexion", SessionCandidatSchema, {
        method: "POST",
        body: JSON.stringify({ email, motDePasse }),
      });

      poserJeton(s.accessToken);
      onConnecte();
    } catch (err) {
      // Le message vient du serveur et ne révèle rien. On ne l'enrichit pas.
      setErreur(err instanceof ErreurApi ? err.message : "Opération impossible.");
    }
  }

  return (
    <section>
      <h2>{inscription ? "Créer un compte candidat" : "Accéder à mon dossier"}</h2>

      <form onSubmit={soumettre} className="saisie-journal">
        {inscription && (
          <label className="large">
            Nom
            <input value={nom} onChange={(e) => setNom(e.target.value)} required />
          </label>
        )}

        <label className="large">
          Adresse électronique
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />
        </label>

        <label className="large">
          Mot de passe
          <input
            type="password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            autoComplete={inscription ? "new-password" : "current-password"}
            minLength={inscription ? 12 : undefined}
            required
          />
          {inscription && <span className="note">Douze caractères au minimum.</span>}
        </label>

        <button type="submit">{inscription ? "Créer le compte" : "Se connecter"}</button>
      </form>

      {message && <p className="depot">{message}</p>}
      {erreur && <p className="alerte" role="alert">{erreur}</p>}

      <button type="button" className="lien" onClick={() => setInscription(!inscription)}>
        {inscription ? "J'ai déjà un compte" : "Créer un compte"}
      </button>
    </section>
  );
}

function MonDossier() {
  const [avisEnvoi, setAvisEnvoi] = useState<Avis[]>([]);
  const q = useApi(["ma-candidature"], "/recrutement/ma-candidature", CandidatureSchema);

  async function envoyer(id: string) {
    setAvisEnvoi([]);
    try {
      await appeler(
        `/recrutement/ma-candidature/${id}/envoyer`,
        z.object({ statut: z.string(), envoyeeLe: z.string().nullable() }),
        { method: "POST" },
      );
      void q.refetch();
    } catch (err) {
      // ⛔ L'envoi refusé porte TOUS ses motifs, pas seulement le premier.
      if (err instanceof ErreurApi) {
        const detail = err.motif ?? err.message;
        setAvisEnvoi([{ niveau: "Blocage", code: "REC", message: detail }]);
      }
    }
  }

  return (
    <section>
      <h2>Mon dossier</h2>

      {(q.data ?? []).map((c) => (
        <article key={c.id} className="candidature">
          <header>
            <strong>Candidature {c.id.slice(0, 8)}</strong>
            <span className={c.statut === "Brouillon" ? "non" : "oui"}>{c.statut}</span>
          </header>

          <p>
            {c.anneesExperienceDeclarees} année(s) d'expérience déclarée(s)
            {c.passageEnCabinet && " · passage en cabinet d'expertise comptable"}
          </p>

          <h3>Pièces ({c.pieces.length})</h3>
          <ul>
            {c.pieces.map((p) => (
              <li key={p.id}>
                {p.type} — {p.nomFichier}
                {p.periodeCouverte && ` (${p.periodeCouverte.slice(0, 7)})`}
              </li>
            ))}
          </ul>

          <h3>Références ({c.references.length})</h3>
          <ul>
            {c.references.map((r) => (
              <li key={r.id} className={r.estJoignable ? "oui" : "non"}>
                {r.nomContact} — {r.poste}, {r.entreprise}
                {r.estJoignable ? "" : " · non joignable, elle ne compte pas"}
              </li>
            ))}
          </ul>

          {c.statut === "Brouillon" && (
            <button type="button" onClick={() => void envoyer(c.id)}>
              Envoyer mon dossier
            </button>
          )}
        </article>
      ))}

      {(q.data ?? []).length === 0 && (
        <p className="a-faire">Aucune candidature en cours.</p>
      )}

      <ListeAvis avis={avisEnvoi} titre="Envoi refusé" />
    </section>
  );
}

export { AvisSchema };
